// @flow
/**
 * Pure helpers for applying live schedule socket deltas.
 * Kept free of React so unit tests can drive the real logic.
 */

export { deltaTargetsOpenBoard } from './boardIdentity';

// Never Object.assign raw delta over nested user / full card — only safe scalars.
export const PARTICIPANT_DELTA_SAFE_FIELDS = [
  'status',
  'note',
  'scheduledCoach',
  'scheduledCoachId',
  'modifiedDate',
  'scheduleModifiedDate',
  'adhocAddress',
  'adhocAddressGoogleUrl',
  'addressOverrideId',
  'addressOverride',
  'effectiveAddress',
  'skill',
  'skillId',
  'requiresMoDropOff',
  'firstName',
  'lastName',
  'pk',
  'currentEventType',
  'scheduleEventActionType',
  'transportationType',
];

/**
 * True when the actor is the local user — ignore structural echo
 * (optimistic UI already applied).
 */
export function isSelfActor(msg: Object, currentUserId: ?number): boolean {
  if (!msg || msg.actorUserId == null || currentUserId == null) {
    return false;
  }
  return Number(msg.actorUserId) === Number(currentUserId);
}

/**
 * Resolve compound Redux participant id from a delta participant object.
 */
export function compoundParticipantId(p: Object): ?string {
  if (!p) {
    return null;
  }
  if (p.id != null && String(p.id).includes('_')) {
    return String(p.id);
  }
  const userId = p.userId != null ? p.userId : p.user;
  // Only use scalar user if it's a number/string id, not a nested object
  const userIdSafe =
    userId != null && typeof userId === 'object' ? userId.id : userId;
  const schedulePk = p.scheduleId != null ? p.scheduleId : p.schedule;
  if (userIdSafe != null && schedulePk != null) {
    return `${userIdSafe}_${schedulePk}`;
  }
  if (p.id != null) {
    return String(p.id);
  }
  return null;
}

/**
 * Merge a delta participant onto existing Redux state without corrupting nested user.
 */
export function mergeParticipantDelta(existing: Object, raw: Object): Object {
  const next = existing ? Object.assign({}, existing) : {};
  // Preserve nested user object always
  const preservedUser = existing && existing.user;
  const preservedId = existing && existing.id;

  PARTICIPANT_DELTA_SAFE_FIELDS.forEach((field) => {
    if (
      Object.prototype.hasOwnProperty.call(raw, field) &&
      raw[field] !== undefined
    ) {
      next[field] = raw[field];
    }
  });

  // Coach assignment — prefer scheduledCoachId
  if (raw.scheduledCoachId !== undefined) {
    next.scheduledCoach = raw.scheduledCoachId;
  } else if (raw.scheduledCoach !== undefined) {
    // Only accept scalar coach ids, not nested objects we don't understand
    if (typeof raw.scheduledCoach !== 'object' || raw.scheduledCoach === null) {
      next.scheduledCoach = raw.scheduledCoach;
    }
  }

  // Never replace nested user with a scalar userId
  if (preservedUser != null && typeof preservedUser === 'object') {
    next.user = preservedUser;
  } else if (raw.firstName != null || raw.lastName != null) {
    // New participant without nested user — build minimal display user
    next.user = {
      id: raw.userId != null ? raw.userId : raw.user,
      firstName: raw.firstName,
      lastName: raw.lastName,
    };
  }

  if (preservedId != null) {
    next.id = preservedId;
  } else {
    const compound = compoundParticipantId(raw);
    if (compound) next.id = compound;
  }

  return next;
}

/**
 * Apply a remote participant payload onto a normalized participants state slice
 * (mutates a plain object shaped like { list, byId }).
 * Returns the compound ids that were updated/added.
 */
export function applyRemoteParticipantsToState(
  state: { list: string[], byId: Object },
  participants: Array<Object>
): string[] {
  const touched = [];
  (participants || []).forEach((raw) => {
    if (!raw) return;
    // Strip accidental scalar user before merge
    if (raw.user != null && typeof raw.user !== 'object') {
      raw = Object.assign({}, raw);
      delete raw.user;
    }
    const compoundId = compoundParticipantId(raw);
    if (!compoundId) return;
    touched.push(compoundId);

    const existing = state.byId[compoundId];
    const merged = mergeParticipantDelta(existing || { id: compoundId }, raw);
    merged.id = compoundId;
    state.byId[compoundId] = merged;
    if (state.list.indexOf(compoundId) === -1) {
      state.list.push(compoundId);
    }
  });
  return touched;
}

/**
 * Sync coach.scheduledParticipants membership for one participant after a delta.
 * Mutates coachesById in place. Returns true if any list changed.
 */
export function syncCoachMembershipInState(
  coachesById: Object,
  coachIds: Array<number | string>,
  participantId: string,
  scheduledCoachId: ?number
): boolean {
  let changed = false;
  coachIds.forEach((coachId) => {
    const coach = coachesById[coachId];
    if (!coach || !coach.scheduledParticipants) return;
    const has = coach.scheduledParticipants.indexOf(participantId) !== -1;
    const shouldHave =
      scheduledCoachId != null && Number(coachId) === Number(scheduledCoachId);
    if (has && !shouldHave) {
      const idx = coach.scheduledParticipants.indexOf(participantId);
      if (idx !== -1) {
        coach.scheduledParticipants.splice(idx, 1);
        changed = true;
      }
    } else if (!has && shouldHave) {
      coach.scheduledParticipants.splice(0, 0, participantId);
      changed = true;
    }
  });
  return changed;
}
